"use client";

import { useEffect, useCallback } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import styles from './ImageViewer.module.css';

export default function ImageViewer() {
    const { imageViewer, closeImageViewer, nextImage, prevImage } = useChatContext();

    const currentImage = imageViewer ? imageViewer.images[imageViewer.currentIndex] : null;
    const hasMultipleImages = imageViewer && imageViewer.images.length > 1;

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (!imageViewer) return;

        if (event.key === 'Escape') {
            closeImageViewer();
        } else if (event.key === 'ArrowRight') {
            nextImage();
        } else if (event.key === 'ArrowLeft') {
            prevImage();
        }
    }, [imageViewer, closeImageViewer, nextImage, prevImage]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const stopPropagation = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    if (!imageViewer || !currentImage) return null;

    return (
        <div className={styles.viewerOverlay} onClick={closeImageViewer}>
            {/* Close Button */}
            <button className={`${styles.controlBtn} ${styles.closeBtn}`} onClick={closeImageViewer}>
                &times;
            </button>

            {/* Download Button */}
            <a
                href={currentImage}
                download
                className={`${styles.controlBtn} ${styles.downloadBtn}`}
                onClick={stopPropagation}
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
            </a>

            {/* Previous Button */}
            {hasMultipleImages && (
                <button
                    className={`${styles.controlBtn} ${styles.navBtn} ${styles.prevBtn}`}
                    onClick={(e) => { prevImage(); stopPropagation(e); }}
                >
                    &#x276E;
                </button>
            )}

            {/* Next Button */}
            {hasMultipleImages && (
                <button
                    className={`${styles.controlBtn} ${styles.navBtn} ${styles.nextBtn}`}
                    onClick={(e) => { nextImage(); stopPropagation(e); }}
                >
                    &#x276F;
                </button>
            )}

            {/* Image Container */}
            <div className={styles.imageContainer}>
                <img
                    src={currentImage}
                    alt="Image viewer"
                    className={styles.mainImage}
                    onClick={stopPropagation}
                />
            </div>
        </div>
    );
}
